/**
 * Single source of truth for the scaled-mobile embedding contract.
 *
 * DXChart lays out against its own iframe viewport, so a narrow visual rectangle
 * would trigger the vendor's narrow-layout media queries and stack the toolbar and
 * sidebar. Instead the caller gives the iframe a desktop-capable *logical* size and
 * scales the whole browsing context down into the visual rectangle, which keeps the
 * complete desktop composition — chrome, canvas, fonts, icons — proportional.
 *
 * These constants are shared by `ChartViewAdapter`, its tests, and the PlayCanvas
 * caller handoff snippet so the three cannot drift apart.
 */

/** Above the vendor toolbar's observed `max-width: 756px` narrow-layout breakpoint, with margin. */
export const LOGICAL_MIN_WIDTH = 768

/** The minimum height at which the configured desktop composition still fits vertically. */
export const LOGICAL_MIN_HEIGHT = 340

/**
 * Upper bound on a logical dimension that scaling has *amplified* beyond the visual
 * rectangle. Only consulted when `scale < 1`.
 *
 * The scale is driven by whichever visual dimension is proportionally smallest, so a
 * degenerate rectangle blows the *other* logical dimension up without bound: 667x1 would
 * otherwise ask for a ~226,780x340 iframe. Transient near-zero placeholders are normal
 * during rotation and animation, so this is a routine input, not a pathological one.
 *
 * 4096 is a logical CSS allocation safety limit, not a literal GPU texture limit: the
 * physical backing store is `logical x devicePixelRatio`, so the real texture is larger
 * than this number on any DPR > 1 device. It is a conservative allocation ceiling chosen
 * to keep a degenerate rectangle from doing unbounded canvas work, not a measured
 * hardware maximum.
 *
 * A large *unscaled* rectangle is deliberately exempt: at `scale === 1` the logical size
 * is exactly the visual size, so nothing is being amplified and the caller is simply
 * asking for the chart it can already see. Capping there would hide legitimate
 * ultrawide desktop charts such as 5120x1440.
 */
export const LOGICAL_MAX_DIMENSION = 4096

export interface LogicalViewport {
  /** Uniform CSS scale to apply to the iframe element, in (0, 1]. */
  scale: number
  /** Iframe `width` in CSS pixels, before the transform. */
  logicalWidth: number
  /** Iframe `height` in CSS pixels, before the transform. */
  logicalHeight: number
}

/**
 * Converts a positive visual chart rectangle into the logical iframe dimensions and
 * uniform scale that render it at exactly that visual size.
 *
 * Dividing both visual dimensions by the same scale preserves the rectangle's aspect
 * ratio (no letterboxing) while guaranteeing both logical minimums are met.
 *
 * Returns `undefined` for non-positive or non-finite geometry, and for scaled rectangles
 * so extreme in aspect ratio that they would need a logical dimension beyond
 * `LOGICAL_MAX_DIMENSION`. Large unscaled rectangles are always accepted. Callers must
 * treat `undefined` as "no chart to show" rather than falling back to a stale or
 * unscaled rectangle.
 */
export function computeLogicalViewport(
  visualWidth: number,
  visualHeight: number,
): LogicalViewport | undefined {
  if (
    !Number.isFinite(visualWidth) ||
    !Number.isFinite(visualHeight) ||
    visualWidth <= 0 ||
    visualHeight <= 0
  ) {
    return undefined
  }

  const scale = Math.min(1, visualWidth / LOGICAL_MIN_WIDTH, visualHeight / LOGICAL_MIN_HEIGHT)

  const logicalWidth = visualWidth / scale
  const logicalHeight = visualHeight / scale

  // Only guard the scaled case: at scale 1 the logical size *is* the visual size, so a
  // large rectangle is a real desktop chart, not an amplified sliver.
  //
  // Rejecting beats clamping here: clamping to the cap would silently break the
  // "transformed bounds match the visual rectangle" contract, drawing a chart that no
  // longer lines up with its placeholder. A rectangle this degenerate cannot show the
  // desktop composition usefully at any scale, so there is nothing worth rendering.
  if (scale < 1 && (logicalWidth > LOGICAL_MAX_DIMENSION || logicalHeight > LOGICAL_MAX_DIMENSION)) {
    return undefined
  }

  return { scale, logicalWidth, logicalHeight }
}
